'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@//components/Button';
import { Textfield } from '@//containers/Textfield';
import { FetchError } from '@//data/httpClient';
import type { LegacyTrackAssignmentInitiatedEventDataContextPlacement } from '@//data/tracking/legacy-types';
import { useLegacyTrackAssignmentInitiated } from '@//data/tracking/useLegacyTrackAssignmentInitiated';
import useGetVehicleByRegistrationNumberMinimalRateLimited from '@//data/vehicles/useGetVehicleByRegistrationNumberMinimalRateLimited';
import { useMarket } from '@//providers/MarketSettingsProvider';
import { OdometerReadingUnit, odometerReadingUnitByMarket } from '@//utils/odometer';
import { safeLocalStorageGetItem, safeLocalStorageRemoveItem } from '@//utils/safe-storage';
import { type AssignmentSchemaType, assignmentSchema } from './utils';

const getFormLocalStorage = (): AssignmentSchemaType | undefined => {
  if (typeof window !== 'object') return undefined;
  const data = safeLocalStorageGetItem('assignmentForm');
  if (data) {
    return JSON.parse(data) as AssignmentSchemaType;
  }
  return undefined;
};

const clearFormLocalStorage = () => safeLocalStorageRemoveItem('assignmentForm');

const inputMode = 'numeric';
const pattern = '[0-9]*';

interface AssignmentFormProps {
  id: string;
  assignmentPagePath: string;
  trackingPlacement: LegacyTrackAssignmentInitiatedEventDataContextPlacement;
  testId?: string;
}

const AssignmentForm = ({
  id,
  assignmentPagePath,
  trackingPlacement,
  testId,
}: AssignmentFormProps) => {
  const searchParams = useSearchParams();
  const autoProceed = useMemo(() => searchParams.has('autoProceed'), []);

  const t = useTranslations('components.assignmentForm');
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');
  const tValidation = useTranslations('utils.validation');
  const market = useMarket();
  const getVehicleMutation = useGetVehicleByRegistrationNumberMinimalRateLimited();
  const router = useRouter();
  const pathname = usePathname();
  const { trackLegacyAssignmentInitiated } = useLegacyTrackAssignmentInitiated();

  const errorMessage = useMemo(() => {
    if (!getVehicleMutation.error) return undefined;

    if (getVehicleMutation.error instanceof FetchError) {
      switch (getVehicleMutation.error.status) {
        case 429:
          return t('errors.tooManyRequests');
        case 404:
          return t('errors.vehicleNotFound');
        case 500:
          return t('errors.unableToFetch');
        default:
          return t('errors.vehicleNotFound');
      }
    }

    return t('errors.vehicleNotFound');
  }, [getVehicleMutation.error, t]);

  const defaultValues = useMemo(() => getFormLocalStorage(), []);

  const {
    formState: { errors },
    handleSubmit,
    register,
    getValues,
  } = useForm({
    defaultValues,
    resolver: zodResolver(assignmentSchema(market, tValidation)),
  });

  const onSubmit = handleSubmit((data) => {
    // Reset any previous errors before submitting
    if (getVehicleMutation.isError) {
      getVehicleMutation.reset();
    }
    getVehicleMutation.mutate({
      registrationNumber: data.registrationNumber.replace(/\s/g, ''),
      tryResolveFullDataSilent: true,
    });
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  useEffect(() => {
    if (autoProceed) {
      router.replace(pathname);
      onSubmit();
    }
  }, []);

  useEffect(() => {
    if (getVehicleMutation.isSuccess) {
      clearFormLocalStorage();

      const { registrationNumber, odometerReading } = getValues();
      trackLegacyAssignmentInitiated({
        context: {
          type: 'widget',
          placement: trackingPlacement,
        },
        vehicle: getVehicleMutation.data,
        odometerReading: {
          value: odometerReading,
          unit:
            odometerReadingUnitByMarket[market] === OdometerReadingUnit.ScandinavianMile
              ? 'mil'
              : 'km',
        },
      });

      router.push(
        `${assignmentPagePath}/${registrationNumber.replace(/\s/g, '')}/${odometerReading}`,
      );
    }
  }, [getVehicleMutation.isSuccess]);

  const isPending = getVehicleMutation.isPending || getVehicleMutation.isSuccess;

  return (
    <>
      <Textfield
        id={`${id}-input-regnr`}
        testId={testId ? `${testId}-input-regnr` : undefined}
        label={tVehicleGlossary('registrationNumber')}
        licensePlateBadge={true}
        market={market}
        state={
          errors.registrationNumber?.message || getVehicleMutation.isError ? 'error' : undefined
        }
        stateMessage={errors.registrationNumber?.message || errorMessage}
        disabled={isPending}
        onKeyDown={handleKeyDown}
        {...register('registrationNumber')}
      />
      <Textfield
        id={`${id}-input-mileage`}
        type="number"
        testId={testId ? `${testId}-input-mileage` : undefined}
        label={tVehicleGlossary('odometerReading.label')}
        unit={tVehicleGlossary(`odometerReading.units.${odometerReadingUnitByMarket[market]}`)}
        state={errors.odometerReading?.message ? 'error' : undefined}
        stateMessage={errors.odometerReading?.message}
        inputMode={inputMode}
        pattern={pattern}
        disabled={isPending}
        onKeyDown={handleKeyDown}
        {...register('odometerReading', {
          valueAsNumber: true,
        })}
      />
      <Button
        type="button"
        title={t('actions.create.title')}
        aria-label={t('actions.create.title')}
        testId={testId ? `${testId}-create-button` : undefined}
        size="lg"
        variant="primary"
        fullWidth
        onClick={onSubmit}
        disabled={isPending}
        loading={isPending}
      >
        {t('actions.create.label')}
      </Button>
    </>
  );
};

export default AssignmentForm;
